import React, { useMemo } from 'react';
import { Table, Tag, Checkbox, Space } from 'antd';
import type { ColumnsType } from 'antd/es/table';

interface DataTableProps {
  columns: string[];
  data: Record<string, any>[];
  totalRows: number;
  columnTypes?: Record<string, string>;
  selectable?: boolean;
  selectedColumns?: string[];
  onColumnSelect?: (selected: string[]) => void;
  loading?: boolean;
  highlightCells?: Set<string>;
  sortable?: boolean;
}

const typeColorMap: Record<string, string> = {
  numeric: 'blue',
  integer: 'cyan',
  float: 'blue',
  text: 'green',
  string: 'green',
  date: 'orange',
  datetime: 'orange',
  categorical: 'purple',
  boolean: 'magenta',
};

const DataTable: React.FC<DataTableProps> = ({
  columns,
  data,
  totalRows,
  columnTypes = {},
  selectable = false,
  selectedColumns = [],
  onColumnSelect,
  loading = false,
  highlightCells,
  sortable = false,
}) => {
  const tableColumns: ColumnsType<Record<string, any>> = useMemo(() => {
    return columns.map((col) => {
      const dtype = columnTypes[col] || 'text';
      const color = typeColorMap[dtype] || 'default';
      const isNumeric = ['integer', 'float', 'numeric'].includes(dtype);

      const colDef: any = {
        title: (
          <Space direction="vertical" size={2} style={{ width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {selectable && (
                <Checkbox
                  checked={selectedColumns.includes(col)}
                  onChange={(e) => {
                    if (!onColumnSelect) return;
                    if (e.target.checked) {
                      onColumnSelect([...selectedColumns, col]);
                    } else {
                      onColumnSelect(selectedColumns.filter((c) => c !== col));
                    }
                  }}
                />
              )}
              <span style={{ fontWeight: 600 }}>{col}</span>
            </div>
            <Tag color={color} style={{ fontSize: 10, lineHeight: '16px' }}>
              {dtype}
            </Tag>
          </Space>
        ),
        dataIndex: col,
        key: col,
        width: 150,
        ellipsis: true,
        render: (value: any, _record: any, index: number) => {
          const isHighlighted = highlightCells?.has(`${index}_${col}`);
          if (value === null || value === undefined) {
            return <span style={{ color: '#ccc', fontStyle: 'italic' }}>null</span>;
          }
          if (isHighlighted) {
            return (
              <span
                style={{
                  color: '#cf1322',
                  background: '#fff1f0',
                  padding: '1px 4px',
                  borderRadius: 3,
                  border: '1px solid #ffa39e',
                }}
              >
                {String(value)}
              </span>
            );
          }
          return String(value);
        },
      };

      // Task 5: Add sorting
      if (sortable) {
        if (isNumeric) {
          colDef.sorter = (a: any, b: any) => {
            const va = a[col] === null || a[col] === undefined ? -Infinity : Number(a[col]);
            const vb = b[col] === null || b[col] === undefined ? -Infinity : Number(b[col]);
            return va - vb;
          };
        } else {
          colDef.sorter = (a: any, b: any) => {
            const va = a[col] === null || a[col] === undefined ? '' : String(a[col]);
            const vb = b[col] === null || b[col] === undefined ? '' : String(b[col]);
            return va.localeCompare(vb);
          };
        }
      }

      return colDef;
    });
  }, [columns, columnTypes, selectable, selectedColumns, onColumnSelect, highlightCells, sortable]);

  return (
    <div className="card-trendata" style={{ padding: 0, overflow: 'hidden' }}>
      <Table
        columns={tableColumns}
        dataSource={data.map((row, i) => ({ ...row, _key: i }))}
        rowKey="_key"
        size="small"
        scroll={{ x: columns.length * 150, y: 'calc(100vh - 320px)' }}
        pagination={data.length <= 500 ? false : {
          total: data.length,
          pageSize: 500,
          showTotal: (total) => `共 ${total} 行（全表 ${totalRows} 行）`,
          showSizeChanger: false,
        }}
        loading={loading}
        bordered
      />
    </div>
  );
};

export default DataTable;
